import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import type {
  ListeningAssessmentQuestion,
  ListeningQuestionOption,
  ListeningStepComponentProps,
} from "./types";
import { normalizeListeningQuestionType } from "../utils";
import { shuffle } from "../../../../lib/arrayOperations";
import { usePreloadAudioUrls } from "../../hooks/usePreloadAudioUrls";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

type ResolvedOption = {
  id: string;
  label: string;
  audio?: string;
  studentImage?: string;
};

const resolveOptions = (
  question: ListeningAssessmentQuestion,
): ResolvedOption[] => {
  const base = question.options ?? [];

  return base
    .map((option, index): ResolvedOption | null => {
      if (typeof option === "string") {
        return {
          id: String(index + 1),
          label: option,
        };
      }

      const typed = option as ListeningQuestionOption;
      const label = typed.label || `Student ${index + 1}`;
      return {
        id: typed.id || String(index + 1),
        label,
        audio: typed.audio,
        studentImage: typed.studentImage,
      };
    })
    .filter((option): option is ResolvedOption => Boolean(option));
};

export function StudentSelectQuestion({
  assessment,
  isLoading,
  isPreview = false,
  onStepReadyChange,
  onSaveAnswer,
}: ListeningStepComponentProps) {
  const studentSelectQuestions = useMemo(() => {
    const partQuestions = assessment?.part?.questions ?? [];
    const filtered = partQuestions.filter(
      (question) =>
        normalizeListeningQuestionType(question.question_type) ===
        "student_select",
    );
    return shuffle(filtered);
  }, [assessment?.part?.questions]);

  const initialAnswerByQuestionId = useMemo(
    () =>
      Object.fromEntries(
        studentSelectQuestions
          .map((question) => [
            question.question_id,
            assessment?.submissionProgress?.answers_by_question_id?.[
              question.question_id
            ] ?? "",
          ])
          .filter(([, answer]) => Boolean(answer)),
      ),
    [
      assessment?.submissionProgress?.answers_by_question_id,
      studentSelectQuestions,
    ],
  );
  const initialActiveQuestionIndex = useMemo(() => {
    const firstUnansweredIndex = studentSelectQuestions.findIndex(
      (question) =>
        !assessment?.submissionProgress?.answers_by_question_id?.[
          question.question_id
        ],
    );
    return firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0;
  }, [
    assessment?.submissionProgress?.answers_by_question_id,
    studentSelectQuestions,
  ]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(
    initialActiveQuestionIndex,
  );
  const [answerByQuestionId, setAnswerByQuestionId] = useState<
    Record<string, string>
  >(() => initialAnswerByQuestionId);
  const [teacherPlayingQuestionId, setTeacherPlayingQuestionId] = useState<
    string | null
  >(null);
  const [completedPromptQuestionIds, setCompletedPromptQuestionIds] = useState<
    Set<string>
  >(() => new Set(Object.keys(initialAnswerByQuestionId)));

  const safeQuestionIndex = Math.min(
    activeQuestionIndex,
    Math.max(studentSelectQuestions.length - 1, 0),
  );
  const activeQuestion = studentSelectQuestions[safeQuestionIndex];
  const isTeacherAudioPlaying =
    teacherPlayingQuestionId === activeQuestion?.question_id;
  const isPromptPlaybackRequired = Boolean(
    !isPreview &&
    activeQuestion?.question_id &&
    activeQuestion.promptAudio?.trim() &&
    !completedPromptQuestionIds.has(activeQuestion.question_id),
  );
  const isFirstQuestion = safeQuestionIndex === 0;
  const isLastQuestion = safeQuestionIndex >= studentSelectQuestions.length - 1;
  const currentQuestionAnswered = Boolean(
    activeQuestion && answerByQuestionId[activeQuestion.question_id],
  );
  const isComplete = studentSelectQuestions
    .filter((question) => resolveOptions(question).length > 0)
    .every((question) => Boolean(answerByQuestionId[question.question_id]));
  const hasQuestionContent = studentSelectQuestions.some(
    (question) =>
      Boolean(question.prompt?.trim()) ||
      Boolean(question.image?.trim()) ||
      Boolean(question.promptAudio?.trim()) ||
      resolveOptions(question).length > 0,
  );
  const canAdvanceWithoutContent =
    studentSelectQuestions.length === 0 || !hasQuestionContent;

  const resolvedOptions = useMemo(() => {
    if (!activeQuestion) {
      return [];
    }

    return resolveOptions(activeQuestion);
  }, [activeQuestion]);
  const teacher = assessment?.characterGroup?.teacher;
  const teacherImage = teacher?.image ?? "";
  const teacherName = teacher?.name || "Teacher";

  const optionAudioById = useMemo(
    () =>
      new Map(resolvedOptions.map((option) => [option.id, option.audio ?? ""])),
    [resolvedOptions],
  );
  const teacherAudioRef = useRef<HTMLAudioElement | null>(null);
  const studentAudioRef = useRef<HTMLAudioElement | null>(null);
  usePreloadAudioUrls(
    isPreview
      ? []
      : [
          activeQuestion?.promptAudio,
          ...resolvedOptions.map((option) => option.audio),
        ],
  );

  const applyAnswer = (questionId: string, optionId: string) => {
    if (isPromptPlaybackRequired) {
      return;
    }
    setAnswerByQuestionId((current) => ({
      ...current,
      [questionId]: optionId,
    }));
    playOptionAudio(optionId);
    if (optionId) {
      void onSaveAnswer?.(questionId, optionId);
    }
  };

  const playOptionAudio = (optionId: string) => {
    if (teacherAudioRef.current) {
      teacherAudioRef.current.pause();
    }
    setTeacherPlayingQuestionId(null);
    const src = optionAudioById.get(optionId) ?? "";
    if (!src) {
      return;
    }
    if (!studentAudioRef.current) {
      studentAudioRef.current = new Audio();
      if (!isPreview) {
        studentAudioRef.current.crossOrigin = "anonymous";
        studentAudioRef.current.preload = "auto";
      }
    }
    studentAudioRef.current.pause();
    studentAudioRef.current.src = src;
    void studentAudioRef.current.play();
  };

  const handleTeacherAudioPlay = () => {
    studentAudioRef.current?.pause();
    if (!activeQuestion?.question_id) {
      return;
    }
    setTeacherPlayingQuestionId(activeQuestion.question_id);
    setAnswerByQuestionId((current) => {
      if (!current[activeQuestion.question_id]) {
        return current;
      }
      const next = { ...current };
      delete next[activeQuestion.question_id];
      return next;
    });
    void onSaveAnswer?.(activeQuestion.question_id, "");
  };

  const markTeacherPromptComplete = () => {
    if (!activeQuestion?.question_id) {
      return;
    }
    setCompletedPromptQuestionIds((current) => {
      if (current.has(activeQuestion.question_id)) {
        return current;
      }
      const next = new Set(current);
      next.add(activeQuestion.question_id);
      return next;
    });
  };

  useEffect(() => {
    // Stop any in-flight student option audio when the active question changes.
    studentAudioRef.current?.pause();
    if (studentAudioRef.current) {
      studentAudioRef.current.currentTime = 0;
    }
  }, [activeQuestion?.question_id]);

  useEffect(() => {
    return () => {
      studentAudioRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    onStepReadyChange?.(isComplete || canAdvanceWithoutContent);
  }, [canAdvanceWithoutContent, isComplete, onStepReadyChange]);

  useEffect(() => {
    if (isPreview) {
      return;
    }
    if (!activeQuestion?.question_id) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      teacherAudioRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [activeQuestion?.question_id, isPreview]);

  const currentQuestionIsAnswerable = resolvedOptions.length > 0;

  if (isLoading) {
    return null;
  }

  if (!assessment?.part) {
    return <Typography>No content available. You can continue.</Typography>;
  }

  if (!activeQuestion) {
    return <Typography>No questions found. You can continue.</Typography>;
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
          alignContent: "center",
        }}
      >
        {activeQuestion.image && (
          <Box
            component="img"
            src={activeQuestion.image}
            alt={`Student selection image ${safeQuestionIndex + 1}`}
            sx={{ height: "100%", width: "100%", objectFit: "contain" }}
          />
        )}
      </Card>
      <Card
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant="filled"
      >
        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Stack spacing={1} alignItems={"flex-start"} sx={{ minWidth: 0 }}>
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <InstructionAudioButton
                audioUrl={assessment?.studentSelectionQuestionAudio}
                label="Play student selection question instructions audio"
              />
              <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                Instructions:
              </Typography>
            </Stack>
          </Stack>
          <Typography>
            {activeQuestion.prompt || "Listen and select the correct student."}
          </Typography>
          {activeQuestion.promptAudio || teacherImage ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minHeight: 52 }}
            >
              {teacherImage ? (
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    flex: "0 0 52px",
                    border: "2px solid",
                    borderColor: isTeacherAudioPlaying
                      ? "primary.main"
                      : "divider",
                    borderRadius: 1,
                    p: "2px",
                  }}
                >
                  <Box
                    component="img"
                    src={teacherImage}
                    alt={teacherName}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 0.75,
                      display: "block",
                    }}
                  />
                </Box>
              ) : null}
              {activeQuestion.promptAudio ? (
                <audio
                  ref={teacherAudioRef}
                  controls
                  controlsList={
                    isPreview
                      ? undefined
                      : "nodownload noplaybackrate noremoteplayback"
                  }
                  preload={isPreview ? undefined : "auto"}
                  crossOrigin={isPreview ? undefined : "anonymous"}
                  onPlay={handleTeacherAudioPlay}
                  onPause={() => setTeacherPlayingQuestionId(null)}
                  onEnded={() => {
                    setTeacherPlayingQuestionId(null);
                    markTeacherPromptComplete();
                  }}
                  onError={markTeacherPromptComplete}
                  src={activeQuestion.promptAudio}
                  style={{ width: "100%", height: "32px" }}
                />
              ) : null}
            </Stack>
          ) : null}
          <Typography variant="subtitle2" color="text.secondary">
            {activeQuestion
              ? `Question ${safeQuestionIndex + 1} of ${studentSelectQuestions.length}`
              : "No student select questions"}
          </Typography>
          <RadioGroup
            value={answerByQuestionId[activeQuestion.question_id] ?? ""}
            onChange={(event) => {
              if (isPromptPlaybackRequired) {
                return;
              }
              const nextValue = event.target.value;
              applyAnswer(activeQuestion.question_id, nextValue);
            }}
            aria-disabled={isPromptPlaybackRequired}
            sx={{
              alignSelf: "center",
              width: "100%",
              maxWidth: 520,
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1,
            }}
          >
            {resolvedOptions.map((option) => (
              <ButtonBase
                key={option.id}
                disabled={isPromptPlaybackRequired}
                onClick={() => {
                  applyAnswer(activeQuestion.question_id, option.id);
                }}
                sx={{
                  width: "100%",
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor:
                    answerByQuestionId[activeQuestion.question_id] === option.id
                      ? "primary.main"
                      : "divider",
                  opacity: isPromptPlaybackRequired ? 0.55 : 1,
                  p: 0.5,
                }}
              >
                <input
                  type="radio"
                  name={`student-select-${activeQuestion.question_id}`}
                  value={option.id}
                  checked={
                    answerByQuestionId[activeQuestion.question_id] === option.id
                  }
                  onChange={() => {
                    applyAnswer(activeQuestion.question_id, option.id);
                  }}
                  style={{ display: "none" }}
                />
                {option.studentImage ? (
                  <Box
                    component="img"
                    src={option.studentImage}
                    alt={option.label}
                    sx={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      objectFit: "cover",
                      borderRadius: 0.75,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      borderRadius: 0.75,
                      backgroundColor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1,
                    }}
                  >
                    <Typography variant="body2">{option.label}</Typography>
                  </Box>
                )}
              </ButtonBase>
            ))}
          </RadioGroup>

          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="space-between"
            sx={{ mt: "auto" }}
          >
            <Button
              variant="outlined"
              disabled={isFirstQuestion}
              onClick={() =>
                setActiveQuestionIndex((previous) => Math.max(previous - 1, 0))
              }
              size="medium"
            >
              Previous
            </Button>
            {!isComplete ? (
              <Button
                variant="contained"
                disabled={
                  isLastQuestion ||
                  isPromptPlaybackRequired ||
                  (currentQuestionIsAnswerable && !currentQuestionAnswered)
                }
                onClick={() =>
                  setActiveQuestionIndex((previous) =>
                    Math.min(previous + 1, studentSelectQuestions.length - 1),
                  )
                }
                size="medium"
              >
                Next
              </Button>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
